import { Text } from "@/components/podkit/typography/Text";
import { useEnvironment } from "@/queries/environment-queries";
import { useCallback, type FC } from "react";
import { Notification } from "@/components/flexkit/badge/Notification";
import { pluralize } from "@/utils/strings";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Button } from "@/components/flexkit/Button";
import { Check, Copy } from "lucide-react";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { UnpushedCommitsSection } from "@/routes/environments/details/GitStatusPanel";

type GitStatusPanelCompactProps = {
    environmentId: string;
};

export const GitStatusPanelCompact: FC<GitStatusPanelCompactProps> = ({ environmentId }) => {
    const { data: environment } = useEnvironment(environmentId);
    const gitStatus = environment?.status?.content?.git;
    const { toast } = useToast();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyBranch = useCallback(async () => {
        if (!gitStatus?.branch) return;
        try {
            await navigator.clipboard.writeText(gitStatus.branch);
            setCopied(true);
            toast({ title: "Branch copied to clipboard", description: gitStatus.branch });
        } catch {
            // swallow; clipboard may be unavailable in some contexts
        }
    }, [gitStatus?.branch, toast, setCopied]);

    if (!gitStatus) {
        return null;
    }

    return (
        <div>
            <div className="flex w-full items-center justify-between rounded-xl pb-2.5">
                <span className="mr-2 select-none text-start text-md font-medium">Git Status</span>
                {gitStatus.totalChangedFiles > 0 && (
                    <Notification
                        className="flex min-h-6 select-none items-center truncate"
                        content={`${gitStatus.totalChangedFiles} ${pluralize(gitStatus.totalChangedFiles, "change")}`}
                    />
                )}
            </div>
            <div>
                <Text className="select-none font-mono text-base text-content-muted">Branch</Text>
                <div className="flex items-center gap-2 pb-2">
                    <Text className="text-base text-content-primary">{gitStatus.branch}</Text>
                    <Button
                        variant="ghost"
                        type="button"
                        className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                        onClick={handleCopyBranch}
                        aria-label={`Copy branch ${gitStatus.branch}`}
                        data-tracking-id="copy-branch-git-status-panel-compact"
                    >
                        {copied ? (
                            <Check className="text-content-success" aria-hidden size={16} />
                        ) : (
                            <Copy aria-hidden size={16} />
                        )}
                    </Button>
                </div>
                <UnpushedCommitsSection gitStatus={gitStatus} />
            </div>
        </div>
    );
};
